import { useState } from "react";
import { Copy, Mail, MessageCircle } from "lucide-react";
import { Modal } from "./Modal";
import { intakeInvitations, type IntakeInvitation } from "../lib/api";

export function SendIntakeDialog({
  open,
  onClose,
  clientId,
  defaultEmail,
  defaultPhone,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  defaultEmail?: string;
  defaultPhone?: string;
}) {
  const [channel, setChannel] = useState<"sms" | "email" | "manual_link">("email");
  const [recipient, setRecipient] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<IntakeInvitation | null>(null);

  function pickChannel(c: "sms" | "email" | "manual_link") {
    setChannel(c);
    if (c === "sms") setRecipient(defaultPhone || "");
    else if (c === "email") setRecipient(defaultEmail || "");
    else setRecipient("");
  }

  async function send() {
    setBusy(true); setError(null);
    try {
      const inv = await intakeInvitations.send(clientId, channel, recipient);
      setSent(inv);
    } catch (e: any) {
      setError(e?.message || "Could not send");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send intake link"
      footer={
        sent ? (
          <button className="primary-button" onClick={onClose}>Done</button>
        ) : (
          <>
            <button onClick={onClose} className="ghost-button">Cancel</button>
            <button className="primary-button" onClick={send} disabled={busy || !recipient}>
              {busy ? "Sending…" : "Send link"}
            </button>
          </>
        )
      }
    >
      {sent ? (
        <div className="intake-sent">
          <p>Link generated. Expires {new Date(sent.expires_at).toLocaleDateString()}.</p>
          <div className="intake-url-row">
            <code>{sent.invite_url || `${window.location.origin}/intake/${sent.token}`}</code>
            <button
              className="icon-button"
              onClick={() => navigator.clipboard.writeText(sent.invite_url || `${window.location.origin}/intake/${sent.token}`)}
              title="Copy link"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="channel-picker">
            <button
              type="button"
              aria-pressed={channel === "email"}
              className={channel === "email" ? "active" : ""}
              onClick={() => pickChannel("email")}
            >
              <Mail size={16} aria-hidden="true" /> Email
            </button>
            <button
              type="button"
              aria-pressed={channel === "sms"}
              className={channel === "sms" ? "active" : ""}
              onClick={() => pickChannel("sms")}
            >
              <MessageCircle size={16} aria-hidden="true" /> SMS
            </button>
            <button
              type="button"
              aria-pressed={channel === "manual_link"}
              className={channel === "manual_link" ? "active" : ""}
              onClick={() => pickChannel("manual_link")}
            >
              <Copy size={16} aria-hidden="true" /> Manual link
            </button>
          </div>

          {channel !== "manual_link" && (
            <label className="form-field">
              <span>{channel === "email" ? "Email address" : "Phone number"}</span>
              <input
                type={channel === "sms" ? "tel" : channel === "email" ? "email" : "text"}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={channel === "email" ? "client@example.com" : "(404) 555-0100"}
              />
            </label>
          )}

          <p className="muted small">
            Link is single-use, expires in 14 days, and routes the client to a focused intake page.
          </p>
          {error && <p className="error-text">{error}</p>}
        </>
      )}
    </Modal>
  );
}
